import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState } from 'react';
import { Playlist } from '@/store/playlist/types';
import { MoreVertical, Edit, Trash2, Play, Eye, Code, Calendar } from 'lucide-react';
import { formatDate } from '@/utils/formatDate';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { cn } from '@/lib/utils';

interface PlaylistCardProps {
  playlist: Playlist;
  onEdit: () => void;
  onShare: () => void;
  onCopyCode: () => void;
  onDelete: () => void;
}

const formatNumber = (num: number): string => {
  if (num >= 1000000) {
    return `${(num / 1000000).toFixed(1)}M`;
  }
  if (num >= 1000) {
    return `${(num / 1000).toFixed(1)}k`;
  }
  return num.toString();
};

export function PlaylistCard({ playlist, onEdit, onShare, onCopyCode, onDelete }: PlaylistCardProps) {
  useLanguage();
  const [isHovered, setIsHovered] = useState(false);
  
  // Check if thumbnail is a placeholder or empty
  const hasValidThumbnail = playlist.thumbnailUrl && 
    playlist.thumbnailUrl !== '/placeholder.svg' && 
    !playlist.thumbnailUrl.includes('placeholder');

  return (
    <div
      className={cn(
        'group bg-white rounded-[18px] overflow-hidden transition-all duration-300 cursor-pointer',
        'border border-[hsl(var(--border))]',
        isHovered && 'shadow-[0_8px_24px_rgba(0,0,0,0.08)] border-[#E2E8F0]'
      )}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      onClick={onEdit}
      aria-label={translateInterfaceText("Abrir playlist {value0}", { value0: (playlist.name) })}
    >
      {/* Thumbnail Premium */}
      <div className="relative w-full h-[150px] md:h-[180px] overflow-hidden">
        {hasValidThumbnail ? (
          <img
            src={playlist.thumbnailUrl}
            alt={playlist.name}
            className={cn(
              'w-full h-full object-cover transition-transform duration-300',
              isHovered && 'scale-[1.02]'
            )}
          />
        ) : (
          /* Placeholder Premium com Gradiente */
          <div 
            className={cn(
              'w-full h-full flex items-center justify-center transition-all duration-300',
              'bg-gradient-to-br from-[#2563EB] to-[#4F46E5]'
            )}
          >
            <Play 
              className={cn(
                'h-9 w-9 text-white/60 transition-all duration-300',
                isHovered && 'scale-110 text-white/80'
              )} 
              strokeWidth={1.5}
            />
          </div>
        )}
        
        {/* Menu 3 Pontinhos - Glassmorphism */}
        <div className="absolute top-3 right-3 z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                className={cn(
                  'h-8 w-8 rounded-full flex items-center justify-center transition-all duration-200',
                  'bg-white/80 backdrop-blur-sm hover:bg-white',
                  'shadow-[0_2px_8px_rgba(0,0,0,0.1)]'
                )}
                onClick={(e) => e.stopPropagation()}
              >
                <MoreVertical className="h-4 w-4 text-[#64748B]" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent 
              align="end" 
              className={cn(
                'w-48 p-1.5',
                'bg-white/90 backdrop-blur-[10px]',
                'rounded-[14px] border-0',
                'shadow-[0_4px_24px_rgba(0,0,0,0.08)]'
              )}
            >
              <DropdownMenuItem 
                onClick={(e) => { e.stopPropagation(); onEdit(); }}
                className="rounded-lg py-2.5 px-3 cursor-pointer"
              >
                <Edit className="h-4 w-4 mr-2.5 text-[#64748B]" />
                <span className="text-[#0F172A]">{translateInterfaceText("Editar Playlist")}</span>
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={(e) => { e.stopPropagation(); onShare(); }}
                className="rounded-lg py-2.5 px-3 cursor-pointer"
              >
                <Eye className="h-4 w-4 mr-2.5 text-[#64748B]" />
                <span className="text-[#0F172A]">{translateInterfaceText("Visualizar")}</span>
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={(e) => { e.stopPropagation(); onCopyCode(); }}
                className="rounded-lg py-2.5 px-3 cursor-pointer"
              >
                <Code className="h-4 w-4 mr-2.5 text-[#64748B]" />
                <span className="text-[#0F172A]">{translateInterfaceText("Copiar Código")}</span>
              </DropdownMenuItem>
              <DropdownMenuItem 
                onClick={(e) => { e.stopPropagation(); onDelete(); }}
                className="rounded-lg py-2.5 px-3 cursor-pointer text-destructive data-[highlighted]:bg-red-600 data-[highlighted]:text-white"
              >
                <Trash2 className="h-4 w-4 mr-2.5" />
                <span>{translateInterfaceText("Excluir")}</span>
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Informações */}
      <div className="p-4 space-y-2">
        {/* Título */}
        <h3 
          className={cn(
            'font-semibold text-[16px] leading-tight line-clamp-1 transition-colors duration-150',
            'text-[#0F172A]',
            isHovered && 'text-[#2563EB]'
          )}
        >
          {playlist.name || translateInterfaceText("Playlist sem título")}
        </h3>
        
        {/* Meta-informações */}
        <div className="flex items-center flex-wrap gap-x-1.5 gap-y-1 text-[13px] text-[#94A3B8]">
          <span>
            {playlist.videos.length} {playlist.videos.length === 1 ? translateInterfaceText("vídeo") : translateInterfaceText("vídeos")}
          </span>
          <span>•</span>
          <span>{formatNumber(playlist.totalViews)} {" "}{translateInterfaceText("views")}</span>
          <span>•</span>
          <span>{formatNumber(playlist.totalPlays || 0)} {" "}{translateInterfaceText("plays")}</span>
          <span>•</span>
          <span className="flex items-center gap-1">
            <Calendar className="h-3 w-3" />
            {translateInterfaceText("Criado em")}{" "}{formatDate(playlist.createdAt)}
          </span>
        </div>
      </div>
    </div>
  );
}
